/**
 * EmployeeOperationsPanel — one Digital Employee's operation, on one screen (FULL-0100 / PROD-OPSUX-014).
 *
 * The rendering half of `employeeOperations.js`. Four lanes answer MASTER_FLOW §15A's MONITOR/OPERATE
 * questions for one named employee — what it is doing, what is waiting on a person, what is waiting
 * on the customer, what failed — and every row is a link into the surface that already owns the next
 * step: the run page (where an approval, a resume or a retry is actually taken), the ticket, and the
 * fleet-wide queues. Nothing here approves, resumes, retries, stops or executes: this panel has no
 * mutation and imports no mutating API, which is asserted by test.
 *
 * The run-state witness is the shared §14C.1 chip (`WorkOutcomeChip`) with the shared status word
 * (`runStatusLabel`) beside it, so the same stored status reads identically here and on the
 * workforce console. Waits are worded by `formatWaitDuration`, the same formatter the attention
 * inbox and the workforce console use.
 */
import { Link } from "react-router-dom";

import WorkOutcomeChip from "./WorkOutcomeChip";
import { runStatusLabel } from "./runStatusVocabulary";
import { formatWaitDuration, ticketLabel } from "../pages/AttentionInboxPage";
import { failureWindowHours, operationTotals, readOperationLanes } from "./employeeOperations";

function ageMs(at, now) {
  if (!at) return null;
  const parsed = Date.parse(at);
  return Number.isNaN(parsed) ? null : Math.max(0, now - parsed);
}

/** "ממתין כבר …" for a wait, "נכשל לפני …" for a failure. An unknown age says so. */
export function operationAgeText(item, laneKey, now = Date.now()) {
  const duration = formatWaitDuration(ageMs(item?.at, now));
  if (duration == null) return "משך הזמן אינו ידוע";
  return laneKey === "failed" ? `נכשל לפני ${duration}` : `ממתין כבר ${duration}`;
}

function StatTile({ label, value, sub, tone = "text-gray-800" }) {
  return (
    <div className="bg-white rounded-xl border shadow-sm px-3 py-2 space-y-0.5">
      <div className={`text-xl font-semibold ${tone}`}>{value}</div>
      <div className="text-[11px] text-gray-500">{label}</div>
      {sub && <div className="text-[10px] text-gray-400">{sub}</div>}
    </div>
  );
}

function OperationRow({ item, laneKey, now }) {
  // `ticketLabel` is the shared formatter, and its input contract is the run's `ticket_number`.
  // The normalised item holds the same fact under a camelCase name, so it is handed back in the
  // shape that helper reads rather than the formatter being re-implemented here — a row with a
  // number must not render as "no linked ticket" because two modules spelled one field differently.
  const ticket = ticketLabel({ ticket_number: item.ticketNumber });
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-gray-100 bg-gray-50/60 px-2.5 py-2">
      <WorkOutcomeChip status={item.status} testId={`work-outcome-${item.runId}`} />
      <span className="text-xs text-gray-600 whitespace-nowrap">{runStatusLabel(item.status)}</span>
      {laneKey === "human" && (
        // The review bucket also holds a resumable PAUSED run, which the approve/reject routes
        // refuse (agent_runs._APPROVABLE_STATES). Saying which is which is the difference between
        // the operator opening a decision and opening a 409.
        <span
          data-testid={`operation-decision-${item.runId}`}
          data-operation-decision={item.approvable ? "approval" : "resume"}
          className={`text-[11px] px-1.5 py-0.5 rounded-full border whitespace-nowrap ${
            item.approvable
              ? "bg-blue-50 text-blue-700 border-blue-200"
              : "bg-amber-50 text-amber-700 border-amber-200"
          }`}
        >
          {item.approvable ? "ממתין לאישור/דחייה" : "ממתין לחידוש"}
        </span>
      )}
      <span className="text-sm text-gray-700">
        {item.ticketId ? (
          <Link
            to={`/tickets/${item.ticketId}`}
            className="hover:underline"
            aria-label={`${ticket} — ריצה ${item.runId}`}
          >
            {ticket}
          </Link>
        ) : (
          <span className="text-gray-400">{ticket}</span>
        )}
      </span>
      <span className="text-[11px] text-gray-400 whitespace-nowrap">{operationAgeText(item, laneKey, now)}</span>
      <Link
        to={`/agent-runs/${item.runId}`}
        className="ml-auto text-xs text-blue-600 hover:underline whitespace-nowrap"
        aria-label={`פתיחת הריצה ${item.runId}`}
      >
        {laneKey === "failed" ? "לסיבת הכשל" : "לריצה"}
      </Link>
    </li>
  );
}

function LaneSection({ lane, now }) {
  return (
    <section
      aria-label={lane.title}
      data-testid={`operation-lane-${lane.key}`}
      data-lane-present={lane.present ? "true" : "false"}
      data-lane-count={lane.count}
      className="bg-white rounded-xl border shadow-sm p-4 space-y-2"
    >
      <div className="flex flex-wrap items-baseline gap-x-2">
        <h2 className="text-sm font-semibold text-gray-700">{lane.title}</h2>
        <WorkOutcomeChip outcome={lane.outcome} testId={`operation-lane-outcome-${lane.key}`} />
        {/* The count is a count even at zero: a lane holding nothing is a fact about this employee,
            not a missing figure. It is only "not reported" when the source did not send the key. */}
        {lane.present ? (
          <span className="text-xs text-gray-400">{lane.count} ריצות</span>
        ) : (
          <span
            role="alert"
            className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
          >
            המקור לא דיווח על הנתח הזה ({lane.missingBuckets.join(", ")}) — לא ידוע, ולא אפס
          </span>
        )}
      </div>
      <p className="text-[11px] text-gray-400">{lane.description}</p>

      {lane.present && lane.items.length === 0 && (
        <p className="text-sm text-gray-400 italic">{lane.emptyText}</p>
      )}
      {lane.items.length > 0 && (
        <ul className="space-y-1.5">
          {lane.items.map((item) => (
            <OperationRow key={item.runId} item={item} laneKey={lane.key} now={now} />
          ))}
        </ul>
      )}
      {lane.key === "human" && lane.present && lane.count > 0 && (
        <p className="text-[11px] text-gray-400">
          מתוכן {lane.approvableCount} ממתינות לאישור או דחייה, ו־{lane.count - lane.approvableCount} ממתינות
          לחידוש.
        </p>
      )}
    </section>
  );
}

export default function EmployeeOperationsPanel({ payload, now = Date.now() }) {
  const lanes = readOperationLanes(payload, now);
  const totals = operationTotals(lanes);
  const windowHours = failureWindowHours(payload);
  const unknown = (value) => (value == null ? "—" : value);

  return (
    <div className="space-y-4">
      <section aria-label="תמונת מצב תפעולית" className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile
          label="עובד כעת"
          value={unknown(totals.doing)}
          sub="ריצות פעילות או חיבור חי"
          tone="text-violet-700"
        />
        <StatTile
          label="ממתין לאדם"
          value={unknown(totals.human)}
          sub={totals.human == null ? null : `${totals.approvable} לאישור או דחייה`}
          tone="text-orange-700"
        />
        <StatTile label="ממתין ללקוח" value={unknown(totals.customer)} tone="text-blue-700" />
        <StatTile label="נכשלו" value={unknown(totals.failed)} tone="text-red-700" />
        <StatTile
          label="סה״כ פתוחות"
          value={totals.reported ? totals.openTotal : "—"}
          sub={totals.failed > 0 ? `ללא ${totals.failed} הכשלים` : "ללא הכשלים"}
        />
      </section>

      {lanes.map((lane) => (
        <LaneSection key={lane.key} lane={lane} now={now} />
      ))}

      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-400">
        <span>
          {windowHours == null
            ? "חלון הכשלים האחרונים לא דווח על ידי המקור"
            : `הכשלים נספרים בחלון של ${windowHours} השעות האחרונות, כפי שהשרת מצהיר`}
          {" · "}הנתונים מזהים ריצות, מצבים וזמנים בלבד — אין כאן ניתוח, תוכן שיחה או סודות.
        </span>
        <span className="flex flex-wrap gap-3">
          <Link to="/agent-runs" className="text-brand-600 hover:underline">
            לכל הריצות ←
          </Link>
          <Link to="/approval-center" className="text-brand-600 hover:underline">
            למרכז האישורים ←
          </Link>
          <Link to="/attention-inbox" className="text-brand-600 hover:underline">
            לתיבת תשומת הלב ←
          </Link>
        </span>
      </div>
    </div>
  );
}
